package diana.soleil.design.iraniancanadienbazzar.feature.addbusiness

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import diana.soleil.design.iraniancanadienbazzar.core.common.AnalyticsEvents
import diana.soleil.design.iraniancanadienbazzar.core.common.AnalyticsParams
import diana.soleil.design.iraniancanadienbazzar.core.common.AnalyticsTracker
import diana.soleil.design.iraniancanadienbazzar.core.common.AppError
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.data.BusinessDraftSnapshot
import diana.soleil.design.iraniancanadienbazzar.core.data.BusinessDraftStore
import diana.soleil.design.iraniancanadienbazzar.core.data.BusinessRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.CategoryRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.CityController
import diana.soleil.design.iraniancanadienbazzar.core.data.DraftProductSnapshot
import diana.soleil.design.iraniancanadienbazzar.core.data.ImagePickController
import diana.soleil.design.iraniancanadienbazzar.core.data.ProductRepository
import diana.soleil.design.iraniancanadienbazzar.core.model.Category
import diana.soleil.design.iraniancanadienbazzar.core.model.Language
import diana.soleil.design.iraniancanadienbazzar.core.model.Metros
import diana.soleil.design.iraniancanadienbazzar.core.model.LocalizedText
import diana.soleil.design.iraniancanadienbazzar.core.model.PickedImage
import kotlinx.coroutines.FlowPreview
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.debounce
import kotlinx.coroutines.flow.drop
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch
import kotlinx.coroutines.withTimeoutOrNull

/** Where the wizard's final submit stands (drives the plan screen's button/banners/success state). */
enum class AddBusinessSubmitStatus {
    /** Real (non-anonymous) account required — the plan screen tells the guest to sign in. */
    SignInRequired,
    Failed,
    Submitted,
}

/** Submit progress for the wizard's completing action (the draft itself lives in [AddBusinessDraft]). */
data class AddBusinessSubmitState(
    val submitting: Boolean = false,
    val status: AddBusinessSubmitStatus? = null,
    /**
     * The listing WAS submitted but some photos/products didn't make it up (flaky network etc.).
     * Surfaced as a note on the confirmation — the owner can finish from the Dashboard later.
     */
    val partialUploadFailed: Boolean = false,
    /**
     * An existing directory listing whose name matches the draft (BIZ-7 duplicate guard). The plan
     * screen warns and offers "submit anyway" / claim-instead guidance; null = no match found.
     */
    val duplicateName: String? = null,
)

/**
 * Drives the Add / Claim business wizard: holds the current [step] and the accumulated
 * [AddBusinessDraft], exposes the category list for the picker, and validates each step before
 * advancing. One ViewModel spans the whole flow (single nav destination), so the draft survives step
 * navigation. Completing on the Free plan submits the draft as a **pending** `businesses/{id}` doc
 * ([submitFree]), then best-effort uploads the picked media to Storage and writes the drafted
 * products — an admin approves the listing into the directory.
 */
@OptIn(FlowPreview::class)
class AddBusinessViewModel(
    categoryRepository: CategoryRepository,
    private val businessRepository: BusinessRepository,
    private val productRepository: ProductRepository,
    private val imagePicker: ImagePickController,
    private val cityController: CityController,
    private val analytics: AnalyticsTracker,
    private val draftStore: BusinessDraftStore,
) : ViewModel() {

    private val _step = MutableStateFlow(WizardStep.Intro)
    val step: StateFlow<WizardStep> = _step.asStateFlow()

    private val _draft = MutableStateFlow(AddBusinessDraft())
    val draft: StateFlow<AddBusinessDraft> = _draft.asStateFlow()

    private val _submitState = MutableStateFlow(AddBusinessSubmitState())
    val submitState: StateFlow<AddBusinessSubmitState> = _submitState.asStateFlow()

    /** True when a previously saved draft was restored — the Intro screen offers Resume/Discard. */
    private val _hasSavedDraft = MutableStateFlow(false)
    val hasSavedDraft: StateFlow<Boolean> = _hasSavedDraft.asStateFlow()

    init {
        // Restore the persisted TEXT draft (QA data-loss fix — "Save draft" used to just exit and
        // the in-memory draft died). Photos are process-local picks and can't be restored.
        draftStore.load()?.let { saved ->
            _draft.value = saved.toDraft()
            _hasSavedDraft.value = true
        }
        // Auto-save every edit (debounced): nothing typed into the wizard is ever lost, which makes
        // the Photos step's "Save draft" button truthful and Cancel/back safe without a confirm.
        // drop(1) skips persisting the just-restored (or empty initial) value back to disk.
        viewModelScope.launch {
            _draft.drop(1).debounce(AUTOSAVE_DEBOUNCE_MS).collect { draft ->
                if (_submitState.value.status != AddBusinessSubmitStatus.Submitted) {
                    draftStore.save(draft.toSnapshot())
                }
            }
        }
    }

    /** Throws away the saved + in-memory draft (Intro screen's "Discard draft"). */
    fun discardDraft() {
        draftStore.clear()
        _draft.value = AddBusinessDraft()
        _hasSavedDraft.value = false
    }

    override fun onCleared() {
        // Flush the debounce window on exit (QA round-7: leaving within 500ms of the last keystroke
        // dropped it). Saving the CURRENT value is also discard/submit-safe: a discarded draft is
        // empty (save() clears on empty) and a submitted one is guarded below.
        if (_submitState.value.status != AddBusinessSubmitStatus.Submitted) {
            draftStore.save(_draft.value.toSnapshot())
        }
        super.onCleared()
    }

    /** Categories for the Step 1 picker (empty until loaded / on error — the picker just shows none). */
    val categories: StateFlow<List<Category>> =
        categoryRepository.observeCategories()
            .map { (it as? DataResult.Success)?.data.orEmpty() }
            .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), emptyList())

    // --- navigation ---
    fun goTo(step: WizardStep) { _step.value = step }
    fun startNewBusiness() {
        analytics.track(AnalyticsEvents.ADD_BUSINESS_START)
        _step.value = WizardStep.Info
    }

    /** Advance to the next step in order (no-op past the last). */
    fun next() {
        _step.value = WizardStep.entries.getOrElse(_step.value.ordinal + 1) { _step.value }
    }

    /** Go back a step; returns false at [WizardStep.Intro] so the host can pop the nav destination. */
    fun back(): Boolean {
        if (_step.value == WizardStep.Intro) return false
        _step.value = WizardStep.entries[_step.value.ordinal - 1]
        return true
    }

    // --- Step 1: Info ---
    fun setName(language: Language, value: String) { _draft.update { it.copy(name = it.name.with(language, value)) } }
    fun setCategory(categoryId: String) {
        _draft.update {
            it.copy(
                categoryId = categoryId,
                // Answers belong to a category: keep only the ones the new category also asks about.
                attributes = diana.soleil.design.iraniancanadienbazzar.core.model.BusinessAttributeRegistry
                    .keepValidFor(categoryId, it.attributes),
            )
        }
    }
    fun setDescription(language: Language, value: String) { _draft.update { it.copy(description = it.description.with(language, value)) } }
    fun toggleAttribute(key: String) {
        _draft.update {
            it.copy(attributes = if (key in it.attributes) it.attributes - key else it.attributes + key)
        }
    }

    // --- Step 2: Location ---
    fun setAddressLine(value: String) { _draft.update { it.copy(addressLine = value) } }
    fun setCity(value: String) { _draft.update { it.copy(city = value) } }
    fun setProvince(value: String) { _draft.update { it.copy(province = value) } }
    fun setPostalCode(value: String) { _draft.update { it.copy(postalCode = value) } }

    // --- Step 3: Photos --- (system picker → draft; upload happens at submit, once the doc exists)
    fun pickCover() { pickInto { draft, image -> draft.copy(cover = image) } }
    fun pickLogo() { pickInto { draft, image -> draft.copy(logo = image) } }
    fun addGalleryPhoto() {
        if (_draft.value.gallery.size >= AddBusinessDraft.MAX_GALLERY_PHOTOS) return
        pickInto { draft, image -> draft.copy(gallery = draft.gallery + image) }
    }

    fun removeCover() { _draft.update { it.copy(cover = null) } }
    fun removeLogo() { _draft.update { it.copy(logo = null) } }
    fun removeGalleryPhoto(index: Int) {
        _draft.update { it.copy(gallery = it.gallery.filterIndexed { i, _ -> i != index }) }
    }

    private fun pickInto(assign: (AddBusinessDraft, PickedImage) -> AddBusinessDraft) {
        viewModelScope.launch {
            val image = imagePicker.pick() ?: return@launch
            _draft.update { assign(it, image) }
        }
    }

    // --- Step 4: Products & promotions (optional) ---
    fun addProduct(name: String, priceCents: Long, promoted: Boolean) {
        if (name.isBlank() || priceCents <= 0) return
        _draft.update { it.copy(products = it.products + DraftProduct(name.trim(), priceCents, promoted)) }
    }

    fun removeProduct(index: Int) {
        _draft.update { it.copy(products = it.products.filterIndexed { i, _ -> i != index }) }
    }

    // --- Step 5: Plan ---
    fun setPlan(plan: BusinessPlan) {
        analytics.track(AnalyticsEvents.ADD_BUSINESS_COMPLETE, mapOf(AnalyticsParams.PLAN to plan.name))
        _draft.update { it.copy(plan = plan) }
    }

    /**
     * The wizard's completing action: records the Free plan and persists the draft as a pending
     * `businesses/{id}` doc. Success flips [submitState] to [AddBusinessSubmitStatus.Submitted] so
     * the plan screen can show the "pending review" confirmation; failures surface inline (guests
     * get [AddBusinessSubmitStatus.SignInRequired]) instead of silently discarding the submission.
     */
    fun submitFree(force: Boolean = false) {
        if (_submitState.value.submitting || _submitState.value.status == AddBusinessSubmitStatus.Submitted) return
        val draft = _draft.value
        val categoryId = draft.categoryId
        if (!draft.isInfoValid || !draft.isLocationValid || categoryId == null) {
            _submitState.value = AddBusinessSubmitState(status = AddBusinessSubmitStatus.Failed)
            return
        }
        setPlan(BusinessPlan.Free)
        viewModelScope.launch {
            _submitState.value = AddBusinessSubmitState(submitting = true)
            // BIZ-7 duplicate guard: warn (don't block) when a same-named listing already exists in
            // the metro — most "duplicates" are owners who should CLAIM instead. Best-effort with a
            // timeout: an offline/slow directory read must never stall a legitimate submission.
            if (!force) {
                val duplicate = withTimeoutOrNull(DUPLICATE_CHECK_TIMEOUT_MS) {
                    runCatching { findDuplicate(draft) }.getOrNull()
                }
                if (duplicate != null) {
                    _submitState.value = AddBusinessSubmitState(duplicateName = duplicate)
                    return@launch
                }
            }
            val result = businessRepository.submitBusiness(
                name = draft.name,
                categoryId = categoryId,
                description = draft.description,
                addressLine = draft.addressLine,
                city = draft.city,
                province = draft.province,
                postalCode = draft.postalCode,
                // Derive the metro from the entered city so the listing isn't left metro-less (which
                // excludes it from the metro-scoped directory + featured carousel, and disables paid
                // promotion). Fall back to the active city selection when the city matches no metro.
                metroId = Metros.forCity(draft.city)?.id ?: cityController.activeMetroId.value,
                attributes = draft.attributes.toList(),
            )
            when (result) {
                is DataResult.Success -> {
                    // The pending doc exists and the caller is its owner — media + products are
                    // best-effort follow-ups: a failure must never lose the submission itself, so
                    // it only sets the "finish from Dashboard" note on the confirmation.
                    val businessId = result.data
                    val mediaResult = businessRepository.uploadBusinessMedia(
                        businessId = businessId,
                        cover = draft.cover,
                        logo = draft.logo,
                        gallery = draft.gallery,
                    )
                    var productsFailed = false
                    draft.products.forEach { product ->
                        val saved = productRepository.upsertProduct(
                            businessId = businessId,
                            productId = null,
                            name = LocalizedText(en = product.name),
                            description = LocalizedText(),
                            priceCents = product.priceCents,
                            available = true,
                            promoted = product.promoted,
                            property = null,
                        )
                        if (saved is DataResult.Failure) productsFailed = true
                    }
                    _submitState.value = AddBusinessSubmitState(
                        status = AddBusinessSubmitStatus.Submitted,
                        partialUploadFailed = mediaResult is DataResult.Failure || productsFailed,
                    )
                    // The listing is submitted — the saved draft would only resurrect a duplicate.
                    draftStore.clear()
                    _hasSavedDraft.value = false
                }
                is DataResult.Failure -> _submitState.value = AddBusinessSubmitState(
                    status = if (result.error is AppError.Unauthorized) {
                        AddBusinessSubmitStatus.SignInRequired
                    } else {
                        AddBusinessSubmitStatus.Failed
                    },
                )
            }
        }
    }

    /** Clears the duplicate warning so the plan screen returns to the plan cards ("Cancel"). */
    fun dismissDuplicate() {
        if (_submitState.value.duplicateName != null) _submitState.value = AddBusinessSubmitState()
    }

    /**
     * First approved/pending listing in the active metro whose name (any language) matches the
     * draft's, compared case/punctuation-insensitively. Display name returned for the warning copy.
     */
    private suspend fun findDuplicate(draft: AddBusinessDraft): String? {
        val existing = businessRepository.observeBusinesses(cityController.activeMetroId.value)
            .first().let { (it as? DataResult.Success)?.data }.orEmpty()
        val draftNames = listOf(draft.name.en, draft.name.fr, draft.name.fa)
            .map(::normalizeName)
            .filter { it.isNotBlank() }
            .toSet()
        if (draftNames.isEmpty()) return null
        return existing.firstOrNull { business ->
            val names = buildList {
                add(business.name)
                business.nameLocalized?.let { add(it.en); add(it.fr); add(it.fa) }
            }
            names.any { normalizeName(it).isNotBlank() && normalizeName(it) in draftNames }
        }?.name
    }

    /** Records which paid tier the owner wants launch news about (paid plans are interest-capture only). */
    fun registerPlanInterest(plan: BusinessPlan) {
        analytics.track(AnalyticsEvents.PLAN_INTEREST, mapOf(AnalyticsParams.PLAN to plan.name))
        _draft.update { it.copy(paidPlanInterest = plan) }
    }

    private inline fun MutableStateFlow<AddBusinessDraft>.update(block: (AddBusinessDraft) -> AddBusinessDraft) {
        value = block(value)
    }

    private companion object {
        const val AUTOSAVE_DEBOUNCE_MS = 500L
        const val DUPLICATE_CHECK_TIMEOUT_MS = 4_000L
    }
}

/** Case/punctuation-insensitive comparable form of a business name ("Café Naderi!" ≙ "cafe naderi"?
 * — no accent folding, but case/space/punctuation differences all collapse). */
private fun normalizeName(raw: String?): String =
    raw.orEmpty().lowercase().filter { it.isLetterOrDigit() }

// --- persisted-draft mapping (text fields only; picked images are process-local and not restorable) ---

private fun AddBusinessDraft.toSnapshot() = BusinessDraftSnapshot(
    name = name,
    categoryId = categoryId,
    description = description,
    attributes = attributes.toList(),
    addressLine = addressLine,
    city = city,
    province = province,
    postalCode = postalCode,
    products = products.map { DraftProductSnapshot(it.name, it.priceCents, it.promoted) },
)

private fun BusinessDraftSnapshot.toDraft() = AddBusinessDraft(
    name = name,
    categoryId = categoryId,
    description = description,
    attributes = attributes.toSet(),
    addressLine = addressLine,
    city = city,
    province = province,
    postalCode = postalCode,
    products = products.map { DraftProduct(it.name, it.priceCents, it.promoted) },
)
